import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import otpService from '../services/otpService';

interface CustomerVerificationPageProps {
  donationData?: {
    amount: number;
    quantity: number;
    donationType: string;
    ratePerGram: number;
  };
  onVerificationComplete?: (data: { fullName: string; mobileNumber: string }) => void;
}

const CustomerVerificationPage: React.FC<CustomerVerificationPageProps> = ({ 
  donationData, 
  onVerificationComplete 
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    mobileNumber: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [otpResponse, setOtpResponse] = useState<any>(null);
  const [step, setStep] = useState<'mobile' | 'otp'>('mobile');
  const [otp, setOtp] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = t('pages.customerVerification.errors.fullNameRequired');
    } else if (!/^[a-zA-Z\s]+$/.test(formData.fullName.trim())) {
      newErrors.fullName = t('pages.customerVerification.errors.onlyCharacters');
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = t('pages.customerVerification.errors.mobileRequired');
    } else if (!/^[6-9]\d{9}$/.test(formData.mobileNumber.trim())) {
      newErrors.mobileNumber = t('pages.customerVerification.errors.validMobile');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOTP = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setMessage('');
    setErrors({});
    try {
      console.log('📱 Sending OTP for:', formData);
      
      // Call OTP send API
      const response = await otpService.sendOTP({
        mobileNumber: formData.mobileNumber,
        fullName: formData.fullName
      });

      if (response.success) {
        console.log('✅ OTP sent successfully:', response.data);
        console.log('🔑 OTP received:', response.data?.otp);
        setMessage(t('pages.customerVerification.otpSentSuccess') as string);
        setOtpResponse(response.data);
        setStep('otp');
      } else {
        console.error('❌ Failed to send OTP:', response.error);
        setErrors({ 
          general: response.error || t('pages.customerVerification.errors.failedToSendOtp')
        });
      }
    } catch (error: any) {
      console.error('❌ Error sending OTP:', error);
      setErrors({ 
        general: error.message || t('pages.customerVerification.errors.networkError')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp) {
      setErrors({ general: t('pages.customerVerification.errors.pleaseEnterOtp') });
      return;
    }

    if (otp.length !== 6) {
      setErrors({ general: t('pages.customerVerification.errors.sixDigitOtp') });
      return;
    }

    setLoading(true);
    setMessage('');
    setErrors({});

    try {
      console.log('🔐 Verifying OTP for:', formData.mobileNumber);
      
      // Call OTP verify API
      const response = await otpService.verifyOTP({
        mobileNumber: formData.mobileNumber,
        otp: otp
      });

      if (response.success) {
        console.log('✅ OTP verified successfully:', response.data);
        setMessage(t('pages.customerVerification.otpVerifiedSuccess') as string);
        
        // Navigate to next page immediately
        if (onVerificationComplete) {
          onVerificationComplete(formData);
        } else {
          navigate('/enter-amount', { 
            state: { 
              customerData: formData,
              donationData: donationData 
            } 
          });
        }
      } else {
        console.error('❌ Failed to verify OTP:', response.error);
        setErrors({ 
          general: response.error || t('pages.customerVerification.errors.invalidOtp')
        });
      }
    } catch (err: any) {
      console.error('❌ Error verifying OTP:', err);
      setErrors({ 
        general: err.message || t('pages.customerVerification.errors.failedToVerifyOtp')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    setMessage('');
    setErrors({});

    try {
      const response = await otpService.resendOTP({
        mobileNumber: formData.mobileNumber,
        fullName: formData.fullName
      });

      if (response.success) {
        console.log('✅ OTP resent successfully:', response.data);
        setMessage(t('pages.customerVerification.otpResentSuccess') as string);
        setOtpResponse(response.data);
      } else {
        console.error('❌ Failed to resend OTP:', response.error);
        setErrors({ 
          general: response.error || t('pages.customerVerification.errors.failedToResendOtp')
        });
      }
    } catch (err: any) {
      console.error('❌ Error resending OTP:', err);
      setErrors({ 
        general: err.message || t('pages.customerVerification.errors.failedToResendOtp')
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black/20 backdrop-blur-sm flex items-center justify-center" style={{backgroundImage: 'url(/images/Asset5.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed'}}>
      <div className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto px-4 py-4 sm:py-6 md:py-8">
        {/* Language Switcher */}
        <div className="absolute top-4 right-4 z-10">
          <LanguageSwitcher />
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-400 to-orange-500 p-4 sm:p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full border-2 border-white flex items-center justify-center">
                <img 
                  src="/images/Asset2.png" 
                  alt="Temple Logo" 
                  className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                />
              </div>
            </div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white">{t('pages.customerVerification.title')}</h1>
            <p className="text-white/90 text-xs sm:text-sm mt-1">{t('pages.customerVerification.subtitle')}</p>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Donation Summary */}
            {donationData && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-center mb-2 sm:mb-3">
                  <span className="text-blue-600 text-base sm:text-lg mr-2">📋</span>
                  <h3 className="text-base sm:text-lg font-semibold text-blue-800">{t('pages.customerVerification.donationSummary')}</h3>
                </div>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('pages.customerVerification.amount')}:</span>
                    <span className="font-semibold text-gray-800">₹{donationData.amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('pages.customerVerification.quantity')}:</span>
                    <span className="font-semibold text-gray-800">{donationData.quantity} g</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('pages.customerVerification.donationType')}:</span>
                    <span className="font-semibold text-gray-800">{donationData.donationType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('pages.customerVerification.ratePerGram')}:</span>
                    <span className="font-semibold text-gray-800">₹{donationData.ratePerGram.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Form Title */}
            <div className="text-center mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">{t('pages.customerVerification.newDevoteeInfo')}</h2>
              <p className="text-sm sm:text-base text-gray-600">{t('pages.customerVerification.enterDevoteeDetails')}</p>
            </div>

            {/* General Error Message */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                <div className="flex items-center">
                  <span className="text-red-600 text-base sm:text-lg mr-2">⚠️</span>
                  <p className="text-red-800 text-xs sm:text-sm font-medium">{errors.general}</p>
                </div>
              </div>
            )}

            {/* Success Message */}
            {message && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                <div className="flex items-center">
                  <span className="text-green-600 text-base sm:text-lg mr-2">✅</span>
                  <p className="text-green-800 text-xs sm:text-sm font-medium">{message}</p>
                </div>
              </div>
            )}

            {/* OTP Display - Only show in OTP step and if OTP is available */}
            {step === 'otp' && otpResponse?.otp && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 text-center mb-4 sm:mb-6">
                <h3 className="text-blue-800 font-bold mb-2 text-sm sm:text-base flex items-center justify-center">
                  <span className="mr-2">📱</span>
                  {t('pages.customerVerification.verificationCode').toUpperCase()}
                </h3>
                <p className="text-blue-700 text-xs sm:text-sm mb-2">{t('pages.customerVerification.yourOtpIs')}</p>
                <div className="bg-blue-100 rounded-lg p-2 sm:p-3">
                  <span className="text-blue-800 text-2xl sm:text-3xl font-bold tracking-widest">
                    {otpResponse.otp}
                  </span>
                </div>
                <p className="text-blue-700 text-xs mt-2">{t('pages.customerVerification.enterThisCode')}</p>
              </div>
            )}

            {/* Mobile Number Step */}
            {step === 'mobile' && (
              <div className="space-y-4 sm:space-y-6">
                {/* Devotee Full Name */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    {t('pages.customerVerification.devoteeFullName')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-400 text-xs sm:text-sm">👤</span>
                    </div>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder={t('pages.customerVerification.enterFullName') as string}
                      className={`w-full pl-8 sm:pl-10 pr-4 py-2 sm:py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors text-sm sm:text-base ${
                        errors.fullName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.fullName && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.fullName}</p>
                  )}
                </div>

                {/* Devotee Mobile Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('pages.customerVerification.devoteeMobileNumber')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-400 text-sm font-medium">+91</span>
                    </div>
                    <input
                      type="tel"
                      name="mobileNumber"
                      value={formData.mobileNumber}
                      onChange={handleInputChange}
                      placeholder={t('pages.customerVerification.enterMobileNumber') as string}
                      className={`w-full pl-12 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${
                        errors.mobileNumber ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-gray-400 text-sm">📱</span>
                    </div>
                  </div>
                  {errors.mobileNumber && (
                    <p className="mt-1 text-sm text-red-600">{errors.mobileNumber}</p>
                  )}
                </div>
              </div>
            )}

            {/* OTP Step */}
            {step === 'otp' && (
              <div className="space-y-4 sm:space-y-6">
                {/* Customer Info Display */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
                  <h3 className="text-gray-800 font-bold mb-2 text-sm sm:text-base flex items-center">
                    <span className="mr-2">👤</span>
                    {t('pages.customerVerification.verifyMobileNumber')}
                  </h3>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">{t('pages.customerVerification.name')}:</span>
                      <span className="text-gray-800 font-medium">{formData.fullName}</span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">{t('pages.customerVerification.mobile')}:</span>
                      <span className="text-gray-800 font-medium">+91 {formData.mobileNumber}</span>
                    </div>
                  </div>
                </div>

                {/* OTP Input */}
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-800 mb-1 sm:mb-2">
                    {t('pages.customerVerification.verificationCode')}
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder={t('pages.customerVerification.enterOtp') as string}
                    maxLength={6}
                    className="w-full px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors text-sm sm:text-base text-center tracking-widest"
                  />
                </div>

                {/* Resend OTP Link */}
                <div className="text-right">
                  <button
                    onClick={handleResendOTP}
                    disabled={loading}
                    className="text-orange-600 hover:text-orange-700 text-xs sm:text-sm font-medium disabled:opacity-50"
                  >
                    {t('pages.customerVerification.resendOtp')}
                  </button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              {step === 'mobile' && (
                <>
                  <button
                    onClick={handleSendOTP}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    ) : null}
                    {t('pages.customerVerification.sendVerificationCode')}
                  </button>
                </>
              )}

              {step === 'otp' && (
                <>
                  <button
                    onClick={handleVerifyOTP}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    ) : null}
                    {t('pages.customerVerification.verifyOtp')}
                  </button>
                  
                  <button
                    onClick={() => setStep('mobile')}
                    className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                  >
                    {t('pages.customerVerification.backToVerification')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {/* AUGMONT Logo */}
            <div className="bg-white rounded-lg px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">Powered By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center">
                  <img 
                    src="/images/Asset1.png" 
                    alt="AUGMONT GOLD" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
            
            {/* Mandirsetu Initiative */}
            <div className="bg-white rounded-lg px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">An Initiative By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center">
                  <img 
                    src="/images/mandirSetu.png" 
                    alt="An Initiative By Mandirsetu" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerVerificationPage;
